/**
 * Инвентарь: выигранные предметы, которые ещё не превратились в лапки.
 *
 * Пока у каждого предмета всего два действия — активировать или отказаться.
 * Кнопки заведены как одна пара на все типы предметов: обмен, продажа и вывод
 * появятся позже, и разводить оформление до появления самих механик рано.
 */

import { useCallback, useEffect, useState } from "react";

import { formatPaws, type InventoryItemDto } from "@koshkodrop/shared";

import { ApiError, api } from "../lib/api";
import { haptic } from "../lib/telegram";
import { RARITY_COLOR, RARITY_LABEL, rarityTileStyle } from "../games/cases/rarity";
import { useSession } from "../store/session";

const STATUS_LABEL: Record<InventoryItemDto["status"], string> = {
  pending: "ждёт решения",
  activated: "активирован",
  declined: "отклонён",
};

export function InventoryPage() {
  const { setBalance } = useSession();
  const [items, setItems] = useState<InventoryItemDto[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .listInventory()
      .then((data) => setItems(data.items))
      .catch((caught) => {
        setItems([]);
        setError(caught instanceof ApiError ? caught.message : "Не удалось загрузить инвентарь");
      });
  }, []);

  useEffect(load, [load]);

  const resolve = async (item: InventoryItemDto, action: "activate" | "decline") => {
    if (busyId) return;

    setBusyId(item.id);
    setError(null);
    try {
      const result = await api.resolveInventoryItem(item.id, action);
      setBalance(BigInt(result.balance));
      setItems((rows) => rows?.map((row) => (row.id === item.id ? result.item : row)) ?? null);
      if (action === "activate") haptic.success();
      else haptic.select();
    } catch (caught) {
      haptic.error();
      setError(caught instanceof ApiError ? caught.message : "Не удалось выполнить действие");
    } finally {
      setBusyId(null);
    }
  };

  const pending = items?.filter((item) => item.status === "pending") ?? [];
  const resolved = items?.filter((item) => item.status !== "pending") ?? [];
  const pendingValue = pending.reduce((sum, item) => sum + BigInt(item.value), 0n);

  return (
    <div className="space-y-4">
      <section className="rounded-3xl border border-white/5 bg-gradient-to-br from-paws-500/15 via-night-800 to-night-900 p-5">
        <p className="text-[11px] font-bold uppercase tracking-widest text-paws-400">Инвентарь</p>
        <h2 className="mt-1 text-xl font-extrabold leading-tight text-white">
          {pending.length > 0
            ? `${pending.length} ${plural(pending.length, "предмет", "предмета", "предметов")} ждёт решения`
            : "Пока пусто"}
        </h2>
        <p className="mt-2 text-xs leading-relaxed text-white/55">
          {pending.length > 0 ? (
            <>
              Активация превратит предмет в лапки — всего{" "}
              <span className="tabular font-bold text-paws-300">{formatPaws(pendingValue)} 🐾</span>.
            </>
          ) : (
            "Открывайте кейсы: выигранные предметы попадают сюда."
          )}
        </p>
      </section>

      {error && (
        <p className="rounded-2xl bg-lose-500/15 px-4 py-2.5 text-center text-sm font-semibold text-lose-400">
          {error}
        </p>
      )}

      {items === null && <p className="py-8 text-center text-sm text-white/35">Загружаю…</p>}

      {pending.length > 0 && (
        <div className="space-y-2.5">
          {pending.map((item) => (
            <ItemCard
              key={item.id}
              item={item}
              busy={busyId === item.id}
              disabled={busyId !== null}
              onResolve={(action) => void resolve(item, action)}
            />
          ))}
        </div>
      )}

      {resolved.length > 0 && (
        <section className="space-y-2">
          <h3 className="px-1 text-xs font-extrabold uppercase tracking-wide text-white/40">
            История
          </h3>
          <div className="space-y-1.5">
            {resolved.map((item) => (
              <div
                key={item.id}
                className="flex items-center gap-3 rounded-2xl bg-night-850 px-3.5 py-2.5"
              >
                <span className="text-xl leading-none opacity-60">{item.emoji}</span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-white/70">{item.name}</p>
                  <p className="text-[10px] font-semibold text-white/30">
                    {STATUS_LABEL[item.status]}
                  </p>
                </div>
                <span
                  className={`tabular text-sm font-extrabold ${
                    item.status === "activated" ? "text-win-400" : "text-white/25"
                  }`}
                >
                  {item.status === "activated" ? `+${formatPaws(BigInt(item.value))}` : "—"}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

interface CardProps {
  item: InventoryItemDto;
  busy: boolean;
  disabled: boolean;
  onResolve: (action: "activate" | "decline") => void;
}

function ItemCard({ item, busy, disabled, onResolve }: CardProps) {
  const color = RARITY_COLOR[item.rarity];

  return (
    <div className="rounded-3xl border p-4" style={rarityTileStyle(item.rarity)}>
      <div className="flex items-center gap-3.5">
        <span
          className="text-[42px] leading-none"
          style={{ filter: `drop-shadow(0 6px 18px color-mix(in oklab, ${color} 60%, transparent))` }}
        >
          {item.emoji}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-extrabold text-white">{item.name}</p>
          <p
            className="mt-0.5 inline-block rounded-md px-1.5 py-0.5 text-[9px] font-extrabold uppercase tracking-wide"
            style={{ background: `color-mix(in oklab, ${color} 22%, transparent)`, color }}
          >
            {RARITY_LABEL[item.rarity]}
          </p>
        </div>
        <span className="tabular shrink-0 text-base font-extrabold" style={{ color }}>
          {formatPaws(BigInt(item.value))} 🐾
        </span>
      </div>

      <div className="mt-3.5 flex gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onResolve("decline")}
          className="flex-1 rounded-2xl border border-white/12 py-2.5 text-sm font-bold text-white/60 active:scale-[0.97] disabled:opacity-40"
        >
          Отмена
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onResolve("activate")}
          className="flex-[1.6] rounded-2xl py-2.5 text-sm font-extrabold text-night-950 active:scale-[0.97] disabled:opacity-40"
          style={{ background: color }}
        >
          {busy ? "Минуту…" : "Активировать"}
        </button>
      </div>
    </div>
  );
}

function plural(count: number, one: string, few: string, many: string): string {
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return many;
  const mod10 = count % 10;
  if (mod10 === 1) return one;
  if (mod10 >= 2 && mod10 <= 4) return few;
  return many;
}
